import { getManageDependentsForm } from 'api/operations/formService';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { useEffect, useState } from 'react';
import { PersonInfo } from 'types/electionService';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types/dynamicForms';
import SubscriberDelegate from '../subscriberDelegate';

interface DomainData {
  formId: string;
  subscriberDelegate: SubscriberDelegate;
}

export class AddDependentModalStore {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _subscriberDelegate: SubscriberDelegate;
  private readonly _formId: string;
  private _addDependentModal = new ModalDelegate();

  public dynamicFormDelegate = new DynamicFormDelegate({
    currentUserRoles: this._rootStore.userStore.userRoles,
    fetchForm: async (formId: string): Promise<FormObjectFromJSON> => {
      return getManageDependentsForm(
        formId,
        {
          common: this._subscriberDelegate.subscriber.common,
          custom: this._subscriberDelegate.subscriber.custom as unknown as Record<string, unknown>,
        },
        null,
        this._rootStore.userStore.sponsorId,
      );
    },
    onSubmit: this.addDependent.bind(this),
  });

  public get showAddDependentModal(): boolean {
    return this._addDependentModal.isVisible;
  }

  public async openAddDependentModal(): Promise<void> {
    this._addDependentModal.open();
    await this.dynamicFormDelegate.buildForm(this._formId);
  }

  public closeAddDependentModal(): void {
    this.dynamicFormDelegate.resetState();
    this._addDependentModal.close();
  }

  public async addDependent(): Promise<void> {
    if (!this.dynamicFormDelegate.formController) return;

    const newDependentInfo = {
      ...this.dynamicFormDelegate.formAnswers,
      id: '',
    } as unknown as PersonInfo;
    await this._subscriberDelegate.addDependent(newDependentInfo);

    runInAction(() => {
      this.closeAddDependentModal();
    });
  }

  constructor({ formId, subscriberDelegate }: DomainData) {
    this._formId = formId;
    this._subscriberDelegate = subscriberDelegate;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

export function useAddDependentModalStore({ subscriberDelegate, formId }: DomainData): AddDependentModalStore | null {
  const [store, setStore] = useState<AddDependentModalStore | null>(
    () => new AddDependentModalStore({ subscriberDelegate, formId }),
  );

  useEffect(() => {
    autorun(() => {
      setStore(new AddDependentModalStore({ subscriberDelegate, formId }));
    });

    return (): void => {
      setStore(null);
    };
  }, [subscriberDelegate, formId]);
  return store;
}
